import { useCallback, useEffect, useRef, useState } from 'react';
import { Slide, toast } from 'react-toastify';
import { returnCancelButtonStyle, returnToastStyle } from '../eQueueUtils';

const CLICK_THRESHOLD = 3; // кол-во кликов вне виджета до показа тостера
const COUNTDOWN_MS = 1 * 60 * 1000;

const CalledActivityToastContent: React.FC<{
	initialSeconds: number;
	onCancel: () => void;
	onExpire: () => void;
}> = ({ initialSeconds, onCancel, onExpire }) => {
	const [remaining, setRemaining] = useState(initialSeconds);
	const onExpireRef = useRef(onExpire);
	onExpireRef.current = onExpire;

	useEffect(() => {
		const timer = setInterval(() => {
			setRemaining((prev) => {
				if (prev <= 1) {
					clearInterval(timer);
					setTimeout(() => {
						return onExpireRef.current();
					}, 0);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
		return () => {
			return clearInterval(timer);
		};
	}, []);

	const pad = (n: number) => {
		return String(n).padStart(2, '0');
	};
	const m = Math.floor(remaining / 60);
	const s = remaining % 60;

	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
			<span>
				Смена статуса на "Работа с посетителем" из-за действий в
				интерфейсе [{pad(m)}:{pad(s)}]
			</span>
			<button onClick={onCancel} style={returnCancelButtonStyle}>
				Отмена
			</button>
		</div>
	);
};

interface UseCalledActivityDetectorParams {
	isActive: boolean;
	onConfirm: () => void;
}

export const useCalledActivityDetector = ({
	isActive,
	onConfirm,
}: UseCalledActivityDetectorParams) => {
	const clickCountRef = useRef(0);
	const toastIdRef = useRef<string | number | undefined>(undefined);
	const cancelledRef = useRef(false);
	const onConfirmRef = useRef(onConfirm);
	onConfirmRef.current = onConfirm;

	const dismissToast = useCallback(() => {
		if (toastIdRef.current !== undefined) {
			toast.dismiss(toastIdRef.current);
			toastIdRef.current = undefined;
		}
	}, []);

	const showCountdownToast = useCallback(() => {
		toastIdRef.current = toast.success(
			<CalledActivityToastContent
				initialSeconds={Math.floor(COUNTDOWN_MS / 1000)}
				onCancel={() => {
					dismissToast();
					clickCountRef.current = 0;
					cancelledRef.current = true;
				}}
				onExpire={() => {
					dismissToast();
					onConfirmRef.current();
				}}
			/>,
			{
				position: 'top-center',
				autoClose: false,
				hideProgressBar: true,
				closeOnClick: false,
				closeButton: false,
				draggable: false,
				transition: Slide,
				style: returnToastStyle,
			},
		);
	}, [dismissToast]);

	const handleClick = useCallback(
		(e: MouseEvent) => {
			if (toastIdRef.current !== undefined) {
				return;
			}
			if (cancelledRef.current) {
				return;
			}

			const target = e.target as Element | null;
			if (target?.closest('[data-return-widget]')) {
				return;
			}

			clickCountRef.current += 1;
			const count = clickCountRef.current;
			const label =
				count === 1 ? 'по оверлею (виджет открыт)' : 'по странице';

			if (count >= CLICK_THRESHOLD) {
				clickCountRef.current = 0;
				showCountdownToast();
			}
		},
		[showCountdownToast],
	);

	useEffect(() => {
		if (!isActive) {
			clickCountRef.current = 0;
			cancelledRef.current = false;
			dismissToast();
			return;
		}

		document.addEventListener('click', handleClick);
		return () => {
			document.removeEventListener('click', handleClick);
			clickCountRef.current = 0;
			cancelledRef.current = false;
			dismissToast();
		};
	}, [isActive, handleClick, dismissToast]);
};
